import { useEffect } from "react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTASection from "@/components/sections/CTASection";
import FAQAccordion from "@/components/sections/FAQAccordion";
import { Reveal, StaggerGroup, staggerItem } from "@/components/motion/Reveal";
import { motion } from "framer-motion";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import GrowthRing from "@/components/visuals/GrowthRing";
import { brand } from "@/content/site";
import { track } from "@/lib/analytics";
import {
  AlertCircle,
  ArrowRight,
  ArrowLeftRight,
  BarChart3,
  Check,
  ClipboardList,
  FileText,
  Handshake,
  Info,
  Package,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Truck,
  User,
  Users,
  Zap,
  X,
} from "lucide-react";

const heroTrust = [
  "Four consecutive live campaign weeks",
  "Minimum $400/week client-funded Meta media spend",
  "$0 Green Circle campaign management fee during the pilot",
  "Agreed sales fee or commission on eligible sales",
];

const problems = [
  {
    title: "The retailer controls the workload",
    desc: "Your future installation volume depends heavily on work allocated by other businesses.",
  },
  {
    title: "You install the job but do not own the customer",
    desc: "The long-term relationship, referrals and future battery or upgrade opportunities stay with someone else.",
  },
  {
    title: "Sales stop while you are onsite",
    desc: "Good enquiries cool down because the business owner cannot install, quote and follow up at the same time.",
  },
  {
    title: "Your workmanship builds somebody else's brand",
    desc: "The customer remembers the retailer's name even though your team delivered the installation.",
  },
];

const before = [
  "Wait for subcontracted jobs",
  "Limited control over pricing and timing",
  "No ownership of the customer relationship",
  "Marketing happens only when the owner has time",
  "Workmanship builds another brand",
];

const after = [
  "Generate enquiries under your own brand",
  "Use installer-approved landing prices and offers",
  "Build direct customer and referral relationships",
  "Keep sales moving while crews are onsite",
  "Create visibility over future installation workload",
];

const journeyStages = [
  {
    icon: Rocket,
    title: "Campaign",
    desc: "Build and launch the agreed Meta campaign for one priority offer and territory.",
  },
  {
    icon: Search,
    title: "Lead capture",
    desc: "Capture enquiries through an approved landing page or lead pathway with clear consent.",
  },
  {
    icon: User,
    title: "Response",
    desc: "Acknowledge, contact and record every agreed pilot lead.",
  },
  {
    icon: ClipboardList,
    title: "Qualification",
    desc: "Confirm broad fit, location, customer needs, timing and the next appropriate step.",
  },
  {
    icon: FileText,
    title: "Consultation & proposal",
    desc: "Use the client's approved products, prices, claims and proposal framework.",
  },
  {
    icon: Handshake,
    title: "Sales closure",
    desc: "Follow up suitable opportunities and work towards compliant acceptance and deposit under the client's process.",
  },
  {
    icon: Truck,
    title: "Installation-ready handover",
    desc: "Coordinate the agreed documents, pre-approvals and status checks before moving the job into the installer's workflow.",
  },
];

const comparison = [
  {
    capability: "Best for",
    energy: "Retailers with an existing sales capability",
    installer: "Installers building their own branded sales pipeline",
  },
  {
    capability: "Campaign setup and management",
    energy: "Included",
    installer: "Included",
  },
  { capability: "Four live campaign weeks", energy: "Included", installer: "Included" },
  { capability: "Minimum Meta media spend", energy: "$400/week", installer: "$400/week" },
  {
    capability: "Green Circle pilot campaign management fee",
    energy: "$0",
    installer: "$0",
  },
  {
    capability: "Lead response",
    energy: "Client-led initially; optional conversion test",
    installer: "Green Circle-managed from launch within agreed scope",
  },
  {
    capability: "Sales consultation and closure",
    energy: "Client responsibility unless separately agreed",
    installer: "Included within the approved sales framework",
  },
  {
    capability: "Sales administration",
    energy: "Not the primary purpose",
    installer: "Coordinated through to installation-ready handover",
  },
  {
    capability: "Additional commercial fee",
    energy: "None for pilot campaign management",
    installer: "Agreed sales fee or commission on eligible sales",
  },
  {
    capability: "Pilot evidence",
    energy: "Lead and conversion quality",
    installer: "Full-funnel lead, sale and installation-ready performance",
  },
];

const metrics = [
  { value: "4 weeks", label: "consecutive live campaign period" },
  { value: "$400/wk", label: "minimum client-funded Meta media spend" },
  { value: "$0", label: "Green Circle campaign management fee during the pilot" },
  { value: "Agreed fee", label: "sales fee or commission on eligible sales" },
];

const journey4Week = [
  {
    week: "Phase 0",
    title: "Readiness & build",
    desc: "Confirm approvals, products, landing prices, territory, installation capacity, contracts, scripts, CRM, lead consent and handover workflow before advertising starts.",
  },
  {
    week: "Week 1",
    title: "Launch & respond",
    desc: "Launch the campaign, contact enquiries, establish the lead-quality baseline and begin consultations.",
  },
  {
    week: "Week 2",
    title: "Refine & follow up",
    desc: "Improve the audience, creative, qualification, proposal and approved sales conversation.",
  },
  {
    week: "Week 3",
    title: "Convert & prepare",
    desc: "Work suitable opportunities towards acceptance and begin agreed pre-approval and documentation steps.",
  },
  {
    week: "Week 4",
    title: "Close & review",
    desc: "Continue follow-up, complete installation-ready handovers where possible and review the commercial funnel.",
  },
];

const scopeColumns = [
  {
    icon: Handshake,
    title: "Sales support",
    items: [
      "Agreed operating hours and response process",
      "Qualification and needs discovery",
      "Approved proposal preparation",
      "Consultation booking",
      "Structured follow-up",
      "Objection handling within authority",
      "Contract-acceptance coordination",
      "Deposit-status tracking",
    ],
  },
  {
    icon: ClipboardList,
    title: "Pre-installation administration",
    items: [
      "Customer and property details",
      "Electricity bill and NMI collection",
      "Site-information requests",
      "DNSP pre-approval preparation or coordination under client authority",
      "Solar Victoria process guidance",
      "Rebate and finance-status tracking where agreed",
      "Site-inspection booking",
      "Technical-review escalation",
    ],
  },
  {
    icon: Truck,
    title: "Installation-ready handover",
    items: [
      "Accepted quote or contract",
      "Approved products and system scope",
      "Deposit and cooling-off status",
      "Rebate and pre-approval status",
      "Technical-review status",
      "Customer and access notes",
      "Approved variations or outstanding conditions",
      "Clear next action and owner",
    ],
  },
];

const readinessChecklist = [
  "Correct contracting entity and ABN",
  "Appropriate insurance and consumer contracts",
  "NET Approved Seller status where required",
  "Solar Victoria authorised retailer status for relevant Solar Homes participation",
  "SAA solar and battery installer accreditation as applicable",
  "Required electrical licences and safety training",
  "Approved products and incentive eligibility",
  "DNSP connection-approval process",
  "Warranty, complaints and after-sales process",
  "Privacy, consent and approved communication process",
  "Reliable product supply and installation capacity",
];

const goodFit = [
  "Established solar and/or battery installation capability",
  "Strong workmanship and customer service",
  "Clear products, warranties and landing prices",
  "Capacity to install new direct work",
  "Willingness to build under the business's own brand",
  "Appropriate retailer and installer readiness",
  "Fast technical and pricing escalation support",
];

const notFit = [
  "No installation capacity",
  "Unclear contracts, warranties or customer responsibilities",
  "Missing licences or scheme requirements",
  "Unreliable stock or installation timeframes",
  "Expectation of guaranteed sales",
  "Misleading rebate, savings or urgency claims",
  "No process for complaints, cancellations or after-sales support",
];

const funnelStages = [
  "Leads",
  "Contacted",
  "Qualified",
  "Consulted",
  "Quoted",
  "Sold",
  "Installation ready",
];

const measurementCategories = [
  "Media spend and cost per lead",
  "Response speed and contact rate",
  "Qualified rate",
  "Consultation and quote rate",
  "Quote-to-sale rate",
  "Average customer price",
  "Average installer landing value",
  "Cancellation rate",
  "Technical rejection rate",
  "Time from lead to sale",
  "Time from sale to installation-ready handover",
  "Number and value of jobs entering the installation schedule",
];

const outcomeCards = [
  {
    icon: Zap,
    title: "Continue Get Leads",
    desc: "Maintain the campaign and enquiry engine while the client manages sales.",
  },
  {
    icon: Users,
    title: "Continue Full Pipeline",
    desc: "Green Circle continues campaign, lead response, sales follow-up and agreed administration.",
  },
  {
    icon: ShieldCheck,
    title: "Pause & retain the evidence",
    desc: "Stop after the pilot with a clear scorecard and documented lessons.",
  },
];

const faqs = [
  {
    q: "Is this only for solar installers?",
    a: "The primary audience is solar and battery installation businesses. The pilot can test an approved solar PV, battery, combined solar-and-battery or suitable upgrade offer. Related products require separate assessment because the sales, technical and compliance pathway may differ.",
  },
  {
    q: "How is this different from the Energy Growth Pilot?",
    a: "The Energy Growth Pilot is designed mainly to prove campaign and lead quality for retailers with their own sales capability. The Installer Growth Pilot adds Green Circle-managed lead response, sales follow-up, agreed sales closure and installation-readiness administration for installers building a direct pipeline under their own brand.",
  },
  {
    q: "Is the whole service free for four weeks?",
    a: "No. Green Circle's campaign management fee is $0 during the four live campaign weeks. The client funds the advertising media spend, and the agreed sales fee or commission applies to eligible sales. Other approved external costs may apply if included in the proposal.",
  },
  {
    q: "Who sets the customer price?",
    a: "The installer/retailer approves the offered products, inclusions, exclusions, landing prices, variation rules and final pricing authority before launch. Green Circle sells only within that approved framework.",
  },
  {
    q: "When is a sales commission payable?",
    a: "The proposal and service agreement define the commission trigger, attribution period, cancellation treatment, GST position and discount authority. Typically, commission is linked to a compliant sale that has passed the agreed acceptance, technical, deposit and cancellation conditions.",
  },
  {
    q: "Does Green Circle become the solar retailer?",
    a: "Not under the standard pilot. Green Circle acts as the client's outsourced marketing, sales and administration partner. The client remains the contracting retailer and retains responsibility for products, technical design, installation, warranties, compliance and customer obligations unless a different legally reviewed arrangement is expressly documented.",
  },
  {
    q: "Can Green Circle complete the Solar Victoria rebate application for the customer?",
    a: "No. Green Circle can guide the customer through the approved process and track progress, but must not complete the customer's application on their behalf or guarantee eligibility or approval. The authorised retailer remains responsible for its retailer-side obligations.",
  },
  {
    q: "Can you guarantee sales?",
    a: "No. The pilot creates and measures real market evidence. Outcomes depend on the market, offer, pricing, brand position, product availability, competition, approvals, installation capacity and customer demand.",
  },
  {
    q: "What happens to leads generated in Week 4?",
    a: "The proposal defines a post-campaign conversion period so late pilot leads can receive the agreed follow-up and be included in the mature pilot scorecard.",
  },
  {
    q: "What if we are not NETCC approved or Solar Victoria authorised?",
    a: "Eligibility depends on the products, state, scheme and contracting model. Green Circle can assess readiness and identify a separate setup pathway, but campaign sales activity requiring those approvals will not launch until the relevant conditions are met.",
  },
  {
    q: "Do we have to stop subcontracting?",
    a: "No. The pilot is intended to test a direct pipeline alongside the installer's current work. The installer can decide over time how much capacity to allocate to direct and subcontracted jobs.",
  },
];

export default function InstallerGrowthPilot() {
  useEffect(() => {
    track("installer_pilot_view", { path: "/for/installer-growth-pilot" });
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Installer Growth Pilot",
    serviceType:
      "Four-week lead-to-installation-ready sales pilot for solar and battery installers",
    provider: { "@type": "Organization", name: brand.name, url: brand.siteUrl },
    areaServed: "AU",
    description:
      "A four-week lead-to-sales pilot for qualified solar and battery installers. Green Circle manages campaigns, lead response, sales follow-up and installation-ready administration under the client's approved brand and pricing.",
  };

  const primaryCtaProps = {
    "data-testid": "igp-primary-cta",
    onClick: () => track("installer_pilot_cta_click", { position: "hero" }),
  };
  const secondaryAssessCtaProps = {
    "data-testid": "igp-assess-cta",
    onClick: () => track("installer_pilot_assessment_start"),
  };
  const compareCtaProps = {
    "data-testid": "igp-compare-egp-cta",
    onClick: () => track("installer_pilot_comparison_energy_click"),
  };

  return (
    <>
      <SEO
        title="Installer Growth Pilot | Build Your Own Solar & Battery Sales Pipeline | Green Circle Group"
        description="A four-week lead-to-sales pilot for qualified solar and battery installers. Green Circle manages campaigns, lead response, sales follow-up and installation-ready administration under your approved brand and pricing."
        path="/for/installer-growth-pilot"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        data-testid="igp-hero"
        className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24 bg-forest text-white"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-48 -top-32 opacity-40"
        >
          <GrowthRing size={560} duration={80} />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-8 [&_span]:!text-white/60 [&_a]:!text-white/60 hover:[&_a]:!text-white [&_.font-semibold]:!text-white">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "For Tradies", to: "/for" },
                { label: "Installer Growth Pilot", to: "/for/installer-growth-pilot" },
              ]}
            />
          </div>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <Eyebrow dark>
                  For Solar &amp; Battery Installers Ready to Build Their Own Pipeline
                </Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03] text-white text-balance">
                  Build your own pipeline. Sell under your own brand. Install on your own terms.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl">
                  The Installer Growth Pilot helps qualified solar and battery
                  installers begin creating direct work under their own brand.
                  Green Circle runs the campaign, responds to the leads, works
                  to close suitable sales and coordinates the agreed
                  administration needed to hand sold jobs into your
                  installation workflow.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Btn to="/contact?interest=installer-growth-pilot" size="lg" {...primaryCtaProps}>
                    Book a Free Growth Assessment
                  </Btn>
                  <Btn href="#how-pilot-works" variant="ghostLight" size="lg" withArrow>
                    See How the Pilot Works
                  </Btn>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="mt-8 grid sm:grid-cols-2 gap-2.5 max-w-2xl">
                  {heroTrust.map((c) => (
                    <li
                      key={c}
                      className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold text-white"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-lime shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-6 text-sm text-white/60 max-w-2xl">
                  Available only to businesses that meet the required retailer,
                  installer, pricing, capacity and compliance-readiness criteria.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Quality installers should be able to build quality businesses</Eyebrow>
            <SectionHeading className="mt-5">
              Being busy is not the same as owning your pipeline.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              Subcontracted work can keep a crew moving, but the retailer
              usually owns the customer, the brand and the future opportunity.
              When the installer is busy onsite, marketing stops, enquiries
              wait and sales follow-up falls back onto the owner.
            </p>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 gap-5">
            {problems.map((p) => (
              <motion.div
                key={p.title}
                variants={staggerItem}
                className="rounded-[1.75rem] bg-soft/50 border border-border-neutral p-7 md:p-8"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white text-mint">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  {p.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* TRANSFORMATION */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>The shift</Eyebrow>
            <SectionHeading className="mt-5">
              From subcontracted installer to a business with its own forward pipeline.
            </SectionHeading>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <Reveal>
              <div className="rounded-[1.75rem] bg-white border border-border-neutral p-7 md:p-8 h-full">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-soft text-mid-neutral">
                    <X className="h-4 w-4" strokeWidth={3} />
                  </span>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">
                    Before
                  </p>
                </div>
                <ul className="mt-6 space-y-3">
                  {before.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-mid-neutral">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-border-neutral shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-[1.75rem] bg-forest text-white p-7 md:p-8 h-full">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lime text-forest">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-lime">
                    After
                  </p>
                </div>
                <ul className="mt-6 space-y-3">
                  {after.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-white/85">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-lime shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <p className="mt-10 text-mid-neutral leading-relaxed max-w-3xl">
            The goal is not to replace every subcontracted job in four weeks.
            The goal is to prove whether a direct, branded sales pipeline can
            become a reliable second engine for the business.
          </p>
        </div>
      </section>

      {/* WHAT GREEN CIRCLE MANAGES */}
      <section id="how-pilot-works" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>One connected lead-to-installation-ready system</Eyebrow>
            <SectionHeading className="mt-5">
              We keep the front end moving while you stay focused on installation quality.
            </SectionHeading>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {journeyStages.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal
                  key={s.title}
                  delay={(i % 4) * 0.05}
                  className="relative rounded-[1.5rem] border border-border-neutral p-6 hover:border-mint transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-forest text-lime">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-bold text-mid-neutral">
                      Step {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold text-forest">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-mid-neutral leading-relaxed">
                    {s.desc}
                  </p>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10 flex items-start gap-3 rounded-2xl bg-soft border border-border-neutral p-5 max-w-4xl">
            <Info className="h-5 w-5 text-mint shrink-0 mt-0.5" />
            <p className="text-sm text-mid-neutral leading-relaxed">
              Green Circle does not make unapproved technical promises or
              replace the installer's final site assessment, system design,
              electrical review or installation responsibility.
            </p>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Compare pilots</Eyebrow>
            <SectionHeading className="mt-5">
              Choose the pilot that matches who owns the sales process.
            </SectionHeading>
          </div>

          <div className="mt-12 overflow-x-auto rounded-[1.5rem] border border-border-neutral bg-white">
            <table className="w-full min-w-[720px] border-collapse" data-testid="pilot-comparison">
              <thead>
                <tr className="bg-forest text-white">
                  <th className="text-left p-5 font-bold text-sm w-[35%]">Capability</th>
                  <th className="p-5 font-bold text-sm text-left">Energy Growth Pilot</th>
                  <th className="p-5 font-bold text-sm text-left bg-forest">
                    <span className="inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-lime" />
                      Installer Growth Pilot
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr key={row.capability} className={i % 2 ? "bg-soft/50" : "bg-white"}>
                    <td className="p-5 text-sm font-semibold text-dark-neutral align-top">
                      {row.capability}
                    </td>
                    <td className="p-5 text-sm text-mid-neutral align-top">
                      {row.energy}
                    </td>
                    <td className="p-5 text-sm text-forest font-medium align-top">
                      {row.installer}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Btn to="/energy-growth-pilot" variant="secondary" withArrow {...compareCtaProps}>
              Explore Energy Growth Pilot
            </Btn>
            <Btn to="/contact?interest=installer-growth-pilot" {...secondaryAssessCtaProps}>
              Assess My Installer Business
            </Btn>
          </div>
        </div>
      </section>

      {/* COMMERCIAL FRAMEWORK */}
      <section className="bg-forest text-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow dark>A controlled pilot with clear commercial boundaries</Eyebrow>
            <SectionHeading dark className="mt-5">
              You define the installation economics before we sell.
            </SectionHeading>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-2xl bg-white/5 border border-white/10 p-5"
              >
                <div className="text-2xl md:text-3xl font-extrabold text-lime">
                  {m.value}
                </div>
                <div className="mt-2 text-xs md:text-sm text-white/70 leading-snug">
                  {m.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-7">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-lime text-forest">
                <Package className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-extrabold">Installer-approved landing price</h3>
              <p className="mt-3 text-white/80 leading-relaxed">
                Before launch, the installer approves the product packages,
                standard inclusions, exclusions, variation rules, service
                territory, warranties, lead time and landing price required for
                each offer. Green Circle cannot change technical scope or
                pricing without authority.
              </p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-7">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-lime text-forest">
                <Target className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-extrabold">Agreed Green Circle sales fee</h3>
              <p className="mt-3 text-white/80 leading-relaxed">
                The sales fee may be fixed, percentage-based or package-specific
                and is agreed before launch. The customer receives one clear
                total price. The commercial arrangement between Green Circle
                and the client must never appear as a hidden consumer charge.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-lime/10 border border-lime/30 p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-lime">
              Approved customer price
            </p>
            <p className="mt-3 text-lg md:text-xl font-extrabold text-white flex flex-wrap items-center gap-2">
              <span className="rounded-lg bg-white/10 border border-white/20 px-3 py-1">
                Installer-approved landing price
              </span>
              <span className="text-lime">+</span>
              <span className="rounded-lg bg-white/10 border border-white/20 px-3 py-1">
                Agreed Green Circle sales fee or margin
              </span>
            </p>
          </div>

          <p className="mt-8 text-sm text-white/60 max-w-3xl leading-relaxed">
            The exact commission trigger, attribution period, cancellation
            treatment, GST position and discount authority are documented in
            the client proposal and service agreement.
          </p>
        </div>
      </section>

      {/* FOUR-WEEK JOURNEY */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Four-week journey</Eyebrow>
            <SectionHeading className="mt-5">
              Four live weeks to test the market, the sales process and the handover.
            </SectionHeading>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {journey4Week.map((j) => (
              <motion.div
                key={j.week}
                variants={staggerItem}
                className="rounded-[1.5rem] bg-soft/50 border border-border-neutral p-6"
              >
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-mint">
                  {j.week}
                </div>
                <h3 className="mt-2 text-lg font-extrabold text-forest">
                  {j.title}
                </h3>
                <p className="mt-3 text-sm text-mid-neutral leading-relaxed">
                  {j.desc}
                </p>
              </motion.div>
            ))}
          </StaggerGroup>
          <div className="mt-8 flex items-start gap-3 rounded-2xl bg-soft border border-border-neutral p-5 max-w-4xl">
            <Info className="h-5 w-5 text-mint shrink-0 mt-0.5" />
            <p className="text-sm text-mid-neutral leading-relaxed">
              Leads generated near the end of the campaign may require follow-up
              beyond Week 4. The proposal defines the post-campaign conversion
              period used to complete the mature pilot scorecard.
            </p>
          </div>
        </div>
      </section>

      {/* SALES + ADMIN SCOPE */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Sales &amp; administration scope</Eyebrow>
            <SectionHeading className="mt-5">
              More than sales calls: an organised path into the installation workflow.
            </SectionHeading>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {scopeColumns.map((col) => {
              const Icon = col.icon;
              return (
                <Reveal
                  key={col.title}
                  className="rounded-[1.75rem] bg-white border border-border-neutral p-7 h-full"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-lime">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold text-forest">
                    {col.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {col.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-2.5 text-sm text-mid-neutral"
                      >
                        <Check
                          className="h-4 w-4 text-mint shrink-0 mt-0.5"
                          strokeWidth={3}
                        />
                        {it}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section
        data-testid="igp-compliance"
        className="bg-white py-20 md:py-28"
        onMouseEnter={() => track("installer_pilot_compliance_expand")}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Direct sales create direct responsibilities</Eyebrow>
            <SectionHeading className="mt-5">
              A capable installer also needs a compliant retail pathway.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              When an installer begins contracting directly with households, the
              business takes on retailer, consumer, warranty and program
              responsibilities. Green Circle can support the workflow, but
              cannot replace the client's legal entity, approvals, licences,
              technical responsibility or customer obligations.
            </p>
          </div>

          <div className="mt-12 rounded-[1.75rem] border-2 border-mint/40 bg-soft/40 p-7 md:p-9">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-mint" />
              <h3 className="text-xl font-extrabold text-forest">
                Retail-readiness checklist
              </h3>
            </div>
            <ul className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-3">
              {readinessChecklist.map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-3 text-sm text-forest"
                >
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-forest text-lime shrink-0">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 rounded-2xl bg-forest text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <p className="text-white/85 leading-relaxed max-w-2xl">
              <strong className="text-lime">Not ready yet?</strong> Green Circle
              can first assess the gaps and scope a Retail Readiness Setup. Paid
              advertising and sales activity will not launch until the agreed
              readiness conditions are met.
            </p>
            <Btn to="/contact?interest=installer-growth-pilot" variant="primary" withArrow className="shrink-0">
              Book a Readiness Assessment
            </Btn>
          </div>
        </div>
      </section>

      {/* SUITABLE CLIENT */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Suitability</Eyebrow>
            <SectionHeading className="mt-5">
              Is your installation business ready to build its own pipeline?
            </SectionHeading>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <Reveal className="rounded-[1.75rem] bg-white border-2 border-mint/40 p-7 md:p-8">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-lime">
                <Check className="h-6 w-6" strokeWidth={3} />
              </div>
              <h3 className="mt-4 text-xl font-extrabold text-forest">
                Good fit
              </h3>
              <ul className="mt-4 space-y-3">
                {goodFit.map((g) => (
                  <li key={g} className="flex items-start gap-2.5 text-mid-neutral">
                    <Check className="h-4 w-4 text-mint shrink-0 mt-1" strokeWidth={3} />
                    {g}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.08} className="rounded-[1.75rem] bg-white border border-border-neutral p-7 md:p-8">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mid-neutral">
                <X className="h-6 w-6" strokeWidth={3} />
              </div>
              <h3 className="mt-4 text-xl font-extrabold text-forest">
                Not yet a fit
              </h3>
              <ul className="mt-4 space-y-3">
                {notFit.map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-mid-neutral">
                    <X className="h-4 w-4 text-mid-neutral shrink-0 mt-1" />
                    {n}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEASUREMENT */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Measurement</Eyebrow>
            <SectionHeading className="mt-5">
              We measure the whole commercial funnel—not just cost per lead.
            </SectionHeading>
          </div>

          <div className="mt-12 rounded-[2rem] bg-forest text-white p-8 md:p-10">
            <div className="flex items-start gap-3">
              <BarChart3 className="h-6 w-6 text-lime shrink-0 mt-1" />
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-lime">
                  Full commercial funnel
                </p>
                <p className="mt-2 text-white/80 leading-relaxed">
                  We track drop-off and value from first click to installation-ready handover.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-2">
              {funnelStages.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div className="rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs md:text-sm font-semibold whitespace-nowrap">
                    {s}
                  </div>
                  {i < funnelStages.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-white/50" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {measurementCategories.map((m) => (
              <div
                key={m}
                className="rounded-xl border border-border-neutral p-4 text-sm text-forest font-medium"
              >
                <ArrowLeftRight className="h-4 w-4 text-mint inline mr-2" />
                {m}
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-mid-neutral leading-relaxed max-w-4xl">
            Green Circle does not guarantee a fixed number of leads, sales,
            installations, revenue or profit. Performance depends on the
            market, offer, pricing, approvals, brand position, product
            availability, territory, budget, installation capacity and the
            client's ability to honour the agreed customer experience.
          </p>
        </div>
      </section>

      {/* AFTER PILOT */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>After the pilot</Eyebrow>
            <SectionHeading className="mt-5">
              Turn proven activity into an ongoing Installer Growth Partnership.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              If the pilot shows a commercially viable pathway, the system can
              continue through an ongoing campaign and pipeline-management
              subscription, client-funded media spend and the agreed sales-fee
              model. Deliverables, capacity, operating hours, administration
              scope and reporting are confirmed in a separate proposal.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {outcomeCards.map((o) => {
              const Icon = o.icon;
              return (
                <Reveal
                  key={o.title}
                  className="rounded-[1.5rem] bg-white border border-border-neutral p-7"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-lime">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold text-forest">
                    {o.title}
                  </h3>
                  <p className="mt-2 text-sm text-mid-neutral leading-relaxed">
                    {o.desc}
                  </p>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-10 text-forest font-semibold text-lg">
            The pilot creates a decision, not an automatic obligation.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="bg-white py-20 md:py-28"
        onClick={() => track("installer_pilot_faq_expand")}
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <SectionHeading className="mt-5">
              What installers usually ask
            </SectionHeading>
          </div>
          <div className="mt-12">
            <FAQAccordion items={faqs} emitSchema />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-forest overflow-hidden py-24 md:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-24 opacity-40"
        >
          <GrowthRing size={520} colorFrom="#2CD59C" colorTo="#8DFE3F" duration={90} />
        </div>
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Eyebrow dark className="justify-center">
            Free Installer Growth Assessment
          </Eyebrow>
          <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1] text-white text-balance">
            What would change if your next installations came through your own brand?
          </h2>
          <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
            Tell us how your installation business operates today, the work you
            want more of, your current approvals and the capacity you want to
            fill. We will assess whether the Installer Growth Pilot is
            commercially and operationally suitable.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Btn
              to="/contact?interest=installer-growth-pilot"
              size="lg"
              data-testid="igp-final-cta"
              onClick={() => track("installer_pilot_assessment_start", { position: "final-cta" })}
            >
              Assess My Installer Business
            </Btn>
            <a
              href={`mailto:${brand.email}`}
              className="text-white/80 font-semibold hover:text-lime transition-colors"
            >
              {brand.email}
            </a>
          </div>
          <p className="mt-6 text-sm text-white/50 max-w-2xl mx-auto">
            No pressure and no generic agency presentation. The first
            conversation is to understand the business, its approvals, its
            capacity and whether the pilot is a responsible fit.
          </p>
        </div>
      </section>
    </>
  );
}
